import type { QueryClient } from "@tanstack/solid-query";
import { COMPANY_KEY } from "@/api/query-keys";
import type { CompanyV2 } from "./types";

type CompanyQueryData = { company: CompanyV2 };
type CompaniesQueryData = { companies: CompanyV2[] };

/**
 * A key the response does not carry is left alone, rather than written as
 * `undefined` over a value that is still true.
 *
 * The DTOs of this API are not uniform — no two of the five company picks
 * return the same set of columns — so "I wrote it, therefore it comes back" is
 * an assumption, not a guarantee. The first symptom would be the billing
 * section blanking itself after a successful save.
 */
const pick = (company: CompanyV2, keys: readonly string[]) => {
  const picked: Partial<CompanyV2> = {};
  for (const key of keys) {
    if (!(key in company)) continue;
    picked[key as keyof CompanyV2] = company[key as keyof CompanyV2] as never;
  }
  return picked;
};

/**
 * Writes a `PATCH /company` response into the two caches that hold a company,
 * instead of invalidating them (D8).
 *
 * Invalidation is what repaints a field under the cursor: the refetch fired by
 * saving section A can land while the user is typing in section B (EP8). The
 * response already carries the updated company, so there is nothing to go and
 * ask for.
 *
 * Two rules make the write safe:
 *
 * - **only the keys just written** are merged. The PATCH response does not
 *   carry `role` / `is_owner`, which only the authenticated list has, so
 *   replacing an entry would strip the caller's own role from it.
 * - **an absent cache stays absent.** A list that was never fetched must not
 *   be invented here: a one-company list would then be served to whoever asks
 *   next, hiding the user's other workspaces.
 */
export const applyCompanyPatchToCache = (
  client: QueryClient,
  companyId: string,
  company: CompanyV2,
  keys: readonly string[],
) => {
  const written = pick(company, keys);

  client.setQueryData(
    [COMPANY_KEY, companyId],
    (previous: CompanyQueryData | undefined) =>
      previous
        ? { ...previous, company: { ...previous.company, ...written } }
        : previous,
  );

  client.setQueryData(
    [COMPANY_KEY],
    (previous: CompaniesQueryData | undefined) =>
      previous
        ? {
            ...previous,
            companies: previous.companies.map((entry) =>
              entry.id === companyId ? { ...entry, ...written } : entry,
            ),
          }
        : previous,
  );
};
